import { Layout, Table, Typography, Col } from "antd";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import { calculatePercentage, formatDataGeneral, getYearFromDateValue } from "utils/utils";
import { isDpatRegistrationYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { renderDpatEvidenceLink, scoreOnlyIfDocumentLinksComplete } from "utils/renderDpatEvidenceLink";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import { documentsColumn } from "utils/tableColums";

const AAP_PROGRAM = "ArLnAxhykoz";
const AAP_YEAR_ATTRIBUTE = "pWkYqcKukAY";
const AAP_STATUS_DATA_ELEMENT = "SZcHb5mvjJx";
const ROAD_SAFETY_SECTOR = "Roads and Transport";
const IMPLEMENTED_STATUSES = ["Completed", "Abandoned", "Abandonned", "Ongoing"];

function isRegistrationOnOrBeforeSelectedYear(registrationDateRaw, selectedYear) {
  const yearNum = Number(selectedYear);
  if (!Number.isFinite(yearNum)) {
    return false;
  }

  const registrationYear = getYearFromDateValue(registrationDateRaw);
  if (isDpatScoreRelaxationExempt()) {
    return isDpatRegistrationYear(registrationYear, yearNum);
  }
  return registrationYear != null && registrationYear <= yearNum;
}

const TransportationNetworkService = forwardRef(({
  year,
  transportors,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const resolvedYear =
    year != null && typeof year === "object" && !Array.isArray(year)
      ? year.value ?? year.label
      : year;
  const analyticsYear = resolvedYear ?? new Date().getFullYear();
  const ouId =
    typeof districtId === "string"
      ? districtId
      : districtId?.value?.id ?? districtId?.id ?? "";

  const [transportations, setTransportations] = useState([]);
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [scoreiii, setScoreiii] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [maxScore, setMaxScore] = useState(3);

  const { Header, Content } = Layout;
  const { Title, Text } = Typography;

  const transportOperatorsUpToYear = useMemo(() => (
    (transportors?.transportors || []).filter((operator) => (
      isRegistrationOnOrBeforeSelectedYear(
        operator.createdAt || operator.registrationDateRaw || operator.registrationDate,
        analyticsYear,
      )
    ))
  ), [transportors?.transportors, analyticsYear]);

  useEffect(() => {
    if (isAssessmentStarted) return;
    const links = transportors?.links ?? [];
    const rawScorei = transportOperatorsUpToYear.length > 0 ? 1 : 0;
    const rawScoreiii = transportOperatorsUpToYear.length > 0 ? 1 : 0;
    setScorei(scoreOnlyIfDocumentLinksComplete(links, rawScorei));
    setScoreiii(scoreOnlyIfDocumentLinksComplete(links, rawScoreiii));
  }, [year, districtId, transportors, transportOperatorsUpToYear, isAssessmentStarted]);

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI2",
      area: "Basic/ Social Services",
      maxScore,
      transportations,
      transportors: transportOperatorsUpToYear,
      scorei,
      scoreii,
      scoreiii,
      percentage,
    }),
  }));

  const DPAT_SLUG = "sdi2.0-2.6";

  const transportationNetworkColumn = [
    { title: "Evidence of Road Safety Issues/activities in the MTDP", dataIndex: "mtdp", key: "mtdp" },
    { title: "No. of Road Safety interventions / activities in the AAP", dataIndex: "aapRoadsActivities", key: "aapRoadsActivities" },
    { title: "No. of Road Safety interventions / activities in the AAP implemented", dataIndex: "aapRoadsActivitiesImp", key: "aapRoadsActivitiesImp" },
    { title: "Availability of register on public transport facilities", dataIndex: "availability", key: "availability" }
  ];


  const transportatorsColumn = [
    { title: "Registration Date", dataIndex: "registrationDate", key: "registrationDate" },
    { title: "Business", dataIndex: "business", key: "business" },
    { title: "Transport Type", dataIndex: "type", key: "type" },
    { title: "Vehicle Type", dataIndex: "typeVehicle", key: "typeVehicle" },
    { title: "Quantity of Vehicles Owned", dataIndex: "vehicleQuantity", key: "vehicleQuantity" },
    { title: "Number of employees", dataIndex: "noOfEmployee", key: "noOfEmployee" }
  ];

  const documentTableColumns = useMemo(
    () => documentsColumn.map((col) =>
      col.dataIndex === "link"
        ? {
          ...col,
          render: (value) => renderDpatEvidenceLink(value, "View Document"),
        }
        : col
    ),
    [],
  );

  const countImplementedRoadSafetyActivities = (plans, reports) => (
    plans.filter((plan) => {
      const report = reports.find((item) => item.trackedEntity === plan.trackedEntity);
      return report?.dataValues?.some(
        (value) =>
          value.dataElement === AAP_STATUS_DATA_ELEMENT &&
          IMPLEMENTED_STATUSES.includes(value.value),
      );
    }).length
  );

  const getIndicatorsData = useCallback(async () => {
    if (!ouId) return;

    const baseData = transportors?.data || {
      mtdp: "NO",
      aapRoadsActivities: 0,
      aapRoadsActivitiesImp: 0,
      availability: transportOperatorsUpToYear.length > 0 ? "YES" : "NO",
    };

    try {
      const entitiesResp = await axios.get("/tracker/trackedEntities", {
        params: {
          orgUnit: ouId,
          program: AAP_PROGRAM,
          filter: `${AAP_YEAR_ATTRIBUTE}:EQ:${analyticsYear}`,
          pageSize: 5000,
        },
      });

      const approvedPlans = entitiesResp?.data?.instances || [];
      const transportationPlans = formatDataGeneral(
        approvedPlans,
        "Sector",
        ROAD_SAFETY_SECTOR,
      ) || [];

      let implemented = 0;
      if (transportationPlans.length > 0) {
        const eventsResp = await axios.get("/tracker/events", {
          params: {
            program: AAP_PROGRAM,
            orgUnit: ouId,
            pageSize: 10000,
          },
        });

        implemented = countImplementedRoadSafetyActivities(
          transportationPlans,
          eventsResp?.data?.instances || [],
        );
      }

      const approved = transportationPlans.length;
      const percentageValue = approved > 0
        ? Number(calculatePercentage(implemented, approved).toFixed(2))
        : 0;

      setTransportations([{
        ...baseData,
        mtdp: approved > 0 ? "YES" : baseData.mtdp,
        aapRoadsActivities: approved,
        aapRoadsActivitiesImp: implemented,
        availability: transportOperatorsUpToYear.length > 0 ? "YES" : "NO",
      }]);
      setPercentage(percentageValue);
      const links = transportors?.links ?? [];
      setScoreii(scoreOnlyIfDocumentLinksComplete(links, percentageValue >= 60 ? 1 : 0));
    } catch (err) {
      console.error("TransportationNetworkService fetch failed:", err);
      setTransportations([{
        ...baseData,
        availability: transportOperatorsUpToYear.length > 0 ? "YES" : "NO",
      }]);
      setPercentage(0);
      setScoreii(0);
    }
  }, [ouId, analyticsYear, transportors, transportOperatorsUpToYear]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2: getIndicatorsData,
    applySnapshot: (snap) => {
      if (snap.transportations) setTransportations(snap.transportations);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.scoreiii != null) setScoreiii(snap.scoreiii);
      if (snap.percentage != null) setPercentage(snap.percentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, transportors, getIndicatorsData],
  });

  return (
    <Comment
      data={transportations}
      year={analyticsYear}
      districtId={ouId}
      tableCommentedId={`sdi2.0-2.6-${analyticsYear}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={analyticsYear}
            slug={DPAT_SLUG}
            mainTitleStyle={{ marginTop: "30px" }}
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.6i Actual Score" score={scorei} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.6ii Actual Score" score={scoreii} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 2.0-2.6iii Actual Score" score={scoreiii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>Evidence of road safety activities in Plans of the Assembly</Title>
          {transportations && <Table
            columns={transportationNetworkColumn}
            dataSource={transportations || []}
            pagination={false} bordered />}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Transport Operators</Title>
          {<Table
            columns={transportatorsColumn}
            dataSource={transportOperatorsUpToYear}
            pagination={false} bordered />}

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Attached Documents</Title>
          <Table
            columns={documentTableColumns}
            dataSource={transportors?.links}
            rowKey="title"
            pagination={false}
            bordered
          />

          <Title level={5} style={{ marginTop: "30px" }}>Conclusion</Title>
          <Content>
            {percentage}% of Road Safety interventions in the Annual Action Plan were implemented
          </Content>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default TransportationNetworkService;
